import type { Row, RowData, Table } from '@tanstack/react-table';
import { useEffect, type ReactNode } from 'react';

import { cn, isEventFromFocusableDescendant } from '@/shared/helpers';

import { DataTable, type RowClickHandler } from '../data-table';
import { CellEditingProvider } from './core/cell-editing-context';
import type { CellEditingStore } from './core/create-cell-editing-store';
import type { StagedValueStore } from './core/create-staged-value-store';
import { StagedValueProvider } from './core/staged-value-context';
import { RowActionBar } from './row/row-action-bar';
import { RowEditingProvider, type RowEditingContextValue } from './row/row-editing-context';

const sharedTableClassName =
  '[&_tbody_tr:hover]:bg-transparent [&_tbody_tr[data-state=selected]]:bg-transparent';
const editingRowClassName = 'bg-primary/10 hover:!bg-primary/10 [&>td:hover]:!bg-transparent';

function isExpandedGroupRow<TData>(row: Row<TData>) {
  if (row.getIsExpanded()) return true;

  let parentRow = row.getParentRow();
  while (parentRow) {
    if (parentRow.getIsExpanded()) return true;
    parentRow = parentRow.getParentRow();
  }

  return false;
}

type CellModeProps = {
  mode: 'cell';
};

type RowModeProps<TData extends RowData> = {
  mode: 'row';
  rowEditingContextValue: RowEditingContextValue;
  editingRowId: string | null;
  isSaving: boolean;
  isDeleting: boolean;
  onClickRow: RowClickHandler<TData>;
  getRowCanClick?: (row: Row<TData>) => boolean;
  onSubmit: () => Promise<void>;
  onDelete?: () => Promise<void>;
  onCancelEdit: () => void;
  getCanEditRow: (row: Row<TData>) => boolean;
  submitLabel?: ReactNode;
  renderExpandedRow?: (row: Row<TData>) => ReactNode;
};

export type EditableDataTableProps<TData extends RowData> = {
  table: Table<TData>;
  cellEditingStore: CellEditingStore;
  stagedValueStore: StagedValueStore;
  className?: string;
  children?: ReactNode;
} & (CellModeProps | RowModeProps<TData>);

type RowModeDataTableProps<TData extends RowData> = Extract<
  EditableDataTableProps<TData>,
  { mode: 'row' }
>;

function RowModeDataTable<TData extends RowData>({
  table,
  rowEditingContextValue,
  editingRowId,
  isSaving,
  isDeleting,
  onClickRow,
  getRowCanClick,
  onSubmit,
  onDelete,
  onCancelEdit,
  submitLabel,
  renderExpandedRow,
  className,
  children,
}: RowModeDataTableProps<TData>) {
  const wrappedRenderExpandedRow = (row: Row<TData>) => {
    const isEditingRow = row.id === editingRowId;
    if (!renderExpandedRow && !isEditingRow) {
      return null;
    }
    return (
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">{renderExpandedRow?.(row)}</div>
        {isEditingRow && (
          <RowActionBar
            isSaving={isSaving}
            isDeleting={isDeleting}
            onCancel={onCancelEdit}
            onSubmit={onSubmit}
            onDelete={onDelete}
            submitLabel={submitLabel}
          />
        )}
      </div>
    );
  };

  useEffect(() => {
    if (editingRowId === null) {
      return undefined;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.defaultPrevented || isEventFromFocusableDescendant(event)) {
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        void onSubmit();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onCancelEdit();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [editingRowId, onCancelEdit, onSubmit]);

  return (
    <RowEditingProvider value={rowEditingContextValue}>
      <DataTable
        table={table}
        className={className}
        onClickRow={onClickRow}
        getRowCanClick={getRowCanClick}
        renderExpandedRow={wrappedRenderExpandedRow}
        getRowClassName={(row) =>
          row.id === editingRowId || isExpandedGroupRow(row) ? editingRowClassName : undefined
        }
      >
        {children}
      </DataTable>
    </RowEditingProvider>
  );
}

export function EditableDataTable<TData extends RowData>(props: EditableDataTableProps<TData>) {
  const { table, cellEditingStore, stagedValueStore, className, children } = props;
  const tableClassName = cn(sharedTableClassName, className);

  return (
    <StagedValueProvider value={stagedValueStore}>
      <CellEditingProvider value={cellEditingStore}>
        {props.mode === 'cell' ? (
          <DataTable table={table} className={tableClassName}>
            {children}
          </DataTable>
        ) : (
          <RowModeDataTable {...props} className={tableClassName} />
        )}
      </CellEditingProvider>
    </StagedValueProvider>
  );
}
